/**
 * Reward screen — post-combat / treasure card + Program choice.
 *
 * Shows gold gained, up to 3 card choices (pick one or skip), and an optional
 * Program. Boss rewards advance the act when taken. All resolution goes through
 * store.takeReward(cardDefId|null, takeProgram).
 */

import { el, div, button } from "../dom";
import { topbar, cardFace } from "../widgets";
import { getProgramDef } from "../../engine";
import type { Store } from "../store";

/** Selected card defId (UI-local). null = none yet. */
let pickedCard: string | null = null;
let takeProg = true;

export function resetRewardUi(): void {
  pickedCard = null;
  takeProg = true;
}

export function renderReward(store: Store): HTMLElement {
  const run = store.run!;
  const reward = run.pendingReward!;
  const isBoss = reward.isBoss === true;

  const screen = div("screen");
  screen.appendChild(topbar(run, () => store.open("deck")));

  const body = div("screen__body scroll");
  body.style.alignItems = "center";

  body.appendChild(
    el("div", {
      class: "h-title center",
      text: isBoss ? "BOSS DEFEATED" : "ICE BREACHED",
    }),
  );
  body.appendChild(
    el("div", {
      class: "h-sub center",
      text: `+ ${reward.gold} ₵`,
      style: "color:var(--amber)",
    }),
  );

  // Program offer.
  if (reward.programChoice) {
    const p = getProgramDef(reward.programChoice);
    const owned = run.programs.includes(p.id);
    const progPanel = div(`panel${takeProg && !owned ? " panel--raised" : ""}`, [
      el("div", { class: "h-sub", text: `PROGRAM — ${p.name}`, style: "color:var(--magenta)" }),
      el("div", {
        class: "muted",
        text: p.description,
        style: "white-space:normal;max-width:480px;line-height:1.4;margin:4px 0",
      }),
      owned
        ? el("div", { class: "muted", text: "Already owned — will be skipped." })
        : div("row", [
            button(
              takeProg ? "btn btn--primary btn--sm" : "btn btn--sm",
              takeProg ? "✓ TAKING" : "TAKE",
              () => {
                takeProg = true;
                store.requestRender();
              },
            ),
            button(
              !takeProg ? "btn btn--primary btn--sm" : "btn btn--sm",
              !takeProg ? "✓ SKIP" : "SKIP",
              () => {
                takeProg = false;
                store.requestRender();
              },
            ),
          ]),
    ]);
    progPanel.style.maxWidth = "560px";
    progPanel.style.width = "100%";
    body.appendChild(progPanel);
  }

  // Card choices.
  if (reward.cardChoices.length > 0) {
    body.appendChild(el("div", { class: "h-sub", text: "CHOOSE A CARD" }));
    const choices = div("row wrap");
    choices.style.justifyContent = "center";
    for (const defId of reward.cardChoices) {
      choices.appendChild(
        cardFace(defId, {
          selected: pickedCard === defId,
          onClick: () => {
            pickedCard = pickedCard === defId ? null : defId;
            store.requestRender();
          },
        }),
      );
    }
    body.appendChild(choices);
  }

  screen.appendChild(body);

  // Actions.
  const actions = div("row");
  actions.style.justifyContent = "center";
  actions.appendChild(
    button("btn btn--primary", pickedCard ? "TAKE CARD ▶" : "TAKE (no card)", () => {
      const card = pickedCard;
      const tp = takeProg;
      resetRewardUi();
      store.takeReward(card, tp);
    }),
  );
  actions.appendChild(
    button("btn btn--ghost", "SKIP ALL", () => {
      const tp = takeProg;
      resetRewardUi();
      store.takeReward(null, tp);
    }),
  );
  screen.appendChild(actions);
  return screen;
}
